<template>
  <!-- 类目搜索 -->
  <div
    class="h5-tab-filters-wrapper"
  >
    <div class="filter-result">
      <div class="filter-result__title">
        Search results for "<span class="filter-result__keyword">{{ categoryName ?? filter.keyword }}</span>"
      </div>
    </div>
    <div
      class="h5-tab-filters"
      ref="tabFiltersRef"
    >
      <div
        v-if="filter.categoryId"
        class="filter-item filter-item__icon"
        @click="showCategroySearch"
      >
        <Icon
          name="search-l"
        />
        <Transition name="category-search-alert">
          <div
            class="category-search-alert"
            v-show="showCategorySearchAlert"
          >
            <img
              src="@/assets/colorIcon/search.svg"
              alt="search"
            >
            Search within category
          </div>
        </Transition>
      </div>
      <div
        @click="openFullFiltersDialog"
        class="filter-item filter-item__icon"
        :class="{ 'filter-item--active': fullFiltersSelectedNums}"
      >
        <Icon
          name="icon-filter"
        />
        <div
          v-if="fullFiltersSelectedNums"
          class="filter-item__nums"
        >
          {{ fullFiltersSelectedNums }}
        </div>
      </div>
      <div class="scroll-wrapper">
        <div
          @click="openSortByDialog"
          class="filter-item"
          :class="{ 'filter-item--active': isSelectedSortBy }"
        >
          Sort By
          <Icon
            class="filter-item__arrow"
            name="arrow-down-l-2"
          />
        </div>
      </div>
    </div>
  </div>
  <H5Dialog
    class="custom-filters-dialog common"
    v-model="sortByDialog"
    height="auto"
  >
    <div class="filter-dialog">
      <div class="header">
        <div class="header-title">
          Sort By
        </div>
        <div
          class="close-button"
          @click="closeSortByDialog"
        >
          <img
            src="@/assets/close-circle-icon.svg"
            width="20px"
            alt=""
          >
        </div>
      </div>
      <div class="content">
        <div
          @click="handleSortByChange(sortByFilters, item)"
          class="content-item"
          v-for="(item, index) in sortByFilters"
          :key="index"
        >
          <div class="content-item-title">
            {{ item.name }}
          </div>
          <div class="content-item-radio">
            <Radio
              :checked="item.checked"
            />
          </div>
        </div>
      </div>
      <div class="footer">
        <div
          class="reset-button"
          @click="resetSortBy"
        >
          Reset
        </div>
        <div
          class="done-button"
          @click="handleSortByDone"
        >
          Done
        </div>
      </div>
    </div>
  </H5Dialog>

  <H5Dialog
    class="custom-filters-dialog"
    v-model="fullFiltersDialog"
    height="100%"
  >
    <div class="filter-dialog full-filters">
      <div class="full-filters-header">
        <div class="header-title">
          Filters
        </div>
        <div
          class="close-button"
          @click="closeFullFiltersDialog"
        >
          <img
            src="@/assets/close-circle-icon.svg"
            width="20px"
            alt=""
          >
        </div>
      </div>
      <div class="full-filters-content">
        <Collapse v-model="collapse">
          <CollapseTrigger
            class="header"
            name="sort-by"
          >
            <div class="header-title">
              Sort By
            </div>
            <div
              class="close-button"
            >
              <Icon :name="collapse.includes('sort-by') ? 'reduce-l' : 'add-l'" />
            </div>
          </CollapseTrigger>
          <CollapseContent
            class="content"
            name="sort-by"
          >
            <div
              @click="handleSortByChange(sortByFilters, item)"
              class="content-item"
              v-for="(item, index) in sortByFilters"
              :key="index"
            >
              <div class="content-item-title">
                {{ item.name }}
              </div>
              <div class="content-item-radio">
                <Radio
                  :checked="item.checked"
                />
              </div>
            </div>
          </CollapseContent>
        </Collapse>
      </div>
      <div class="footer">
        <div
          class="reset-button"
          @click="resetAllFilters"
        >
          Reset
        </div>
        <div
          class="done-button"
          @click="handleFullFiltersDone"
        >
          Done
        </div>
      </div>
    </div>
  </H5Dialog>

  <CategorySearch
    v-model="categorySearchVisible"
    :fixed="fixed"
    :title="categoryName ?? filter.keyword"
  />

  <!-- Hover condition filter -->
  <div
    v-if="isShowHoverFilters"
    @click="openFullFiltersDialog"
    class="filter-item filter-item__hover filter-item__icon"
    :class="{ 'filter-item--active': fullFiltersSelectedNums}"
  >
    <Icon
      name="icon-filter"
    />
    <div
      v-if="fullFiltersSelectedNums"
      class="filter-item__nums"
    >
      {{ fullFiltersSelectedNums }}
    </div>
  </div>
</template>

<script setup>
import H5Dialog from '@/components/H5/H5Dialog/index.vue'
import { buriedPointUtil } from '@/utils/utils'
import { useEventListener, useIntersectionObserver } from '@vueuse/core'
import { useProductList } from '../../hooks/useProductList'
import CategorySearch from './CategorySearch.vue'
import { Icon } from '@/components/Icon'
import Radio from './Radio.vue'
import { Collapse, CollapseContent, CollapseTrigger } from '@/components/Collapse'

const fixed = ref(false)

useEventListener(window, 'scroll', (e) => {
  const scrollTop = e.target.scrollingElement.scrollTop
  if (scrollTop > 120) {
    fixed.value = true
  } else {
    fixed.value = false
  }
})

const tabFiltersRef = useTemplateRef('tabFiltersRef')
const isShowHoverFilters = ref(false)

useIntersectionObserver(
  tabFiltersRef,
  ([entry]) => {
    isShowHoverFilters.value = !entry?.isIntersecting || false
  }
)

const { filter, onReset } = useProductList()

const route = useRoute()

const categoryName = computed(() => route.query.categoryName)

// sort by
const sortByDialog = ref(false)
const sortByFilters = ref([
  { name: 'Best Match', checked: true },
  { name: 'Bestselling', checked: false },
  { name: 'Lowest to Highest Quality', checked: false },
  { name: 'Highest to Lowest Quality', checked: false }
])
const sortByFiltersCopy = ref([...sortByFilters.value])
const sortMap = ref(null)
const isSelectedSortBy = computed(() => sortByFiltersCopy.value.filter(item => item.name !== 'Best Match' && item.checked).length > 0)

const collapse = ref(['sort-by'])

// 完整分类弹窗
const fullFiltersDialog = ref(false)

onMounted(() => {
  setTimeout(() => {
    showCategorySearchAlert.value = false
  }, 5000)
})

const categorySearchVisible = ref(false)
const showCategorySearchAlert = ref(localStorage.getItem('categorySearchAlert') !== 'hidden')
const showCategroySearch = () => {
  showCategorySearchAlert.value = false
  localStorage.setItem('categorySearchAlert', 'hidden')
  categorySearchVisible.value = true
}

// sort by
const openSortByDialog = () => {
  sortByFilters.value = JSON.parse(JSON.stringify(sortByFiltersCopy.value))
  sortByDialog.value = true
}

const closeSortByDialog = () => {
  sortByDialog.value = false
}

const handleSortByChange = (list, item) => {
  list.forEach(item => {
    item.checked = false
  })
  item.checked = true
}

const resetSortBy = () => {
  sortByFilters.value.forEach(item => {
    item.checked = false
    if (item.name === 'Best Match') {
      item.checked = true
    }
  })
}

const handleSortByDone = () => {
  const isChanged = sortByFiltersCopy.value.some((item, index) => {
    return item.checked !== sortByFilters.value[index].checked
  })

  sortByFiltersCopy.value = [...sortByFilters.value]
  const item = sortByFilters.value.find(item => item.checked)

  let isDesc = false
  if (item.name === 'Best Match') {
    sortMap.value = null
    isDesc = true
  } else if (item.name === 'Bestselling') {
    sortMap.value = { monthSold: 'desc' }
    isDesc = true
  } else if (item.name === 'Lowest to Highest Quality') {
    sortMap.value = { price: 'asc' }
    isDesc = false
  } else if (item.name === 'Highest to Lowest Quality') {
    sortMap.value = { price: 'desc' }
    isDesc = true
  }

  // 有改动才埋点
  const others = {
    sortBy: item.name,
    isDesc
  }
  if (others.sortBy === 'Lowest to Highest Quality' || others.sortBy === 'Highest to Lowest Quality') {
    others.sortBy = 'price'
  }
  isChanged && buriedPointUtil({ eventId: 'MPSF202', others })

  filter.sortMap = sortMap.value
  onReset()
  sortByDialog.value = false
}

// 全部分类相关
const openFullFiltersDialog = () => {
  sortByFilters.value = JSON.parse(JSON.stringify(sortByFiltersCopy.value))

  fullFiltersDialog.value = true
}

const closeFullFiltersDialog = () => {
  fullFiltersDialog.value = false
}

const resetAllFilters = () => {
  resetSortBy()
}

const handleFullFiltersDone = () => {
  buriedPointUtil({ eventId: 'MPSF201', others: { categoryId: filter.categoryId, filterKeyword: categoryName.value } })

  handleSortByDone()

  fullFiltersDialog.value = false
}

const fullFiltersSelectedNums = computed(() => {
  let num = 0
  if (isSelectedSortBy.value) {
    num++
  }
  return num
})
</script>

<style lang="less" scoped>
.h5-tab-filters-wrapper {
  padding: 16px 16px 0;
  width: 100%;
  border-radius: 8px 8px 0 0;
  background-color: white;
}

.filter-result {
  font-family: mon-700;
  font-size: 16px;
  color: #232323;

  @media (min-width: 1024px) {
    font-size: 20px;
  }

  &__title {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 24px;
  }

  &__keyword {
    color: #052784;
  }
}

.h5-tab-filters {
  position: relative;
  margin-top: 16px;
  display: flex;

  .scroll-wrapper {
    margin-left: 8px;
    display: flex;
    overflow-x: scroll;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.category-search-alert {
  z-index: 99;
  position: absolute;
  top: calc(100% + 8px);
  left: 0px;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 8px;
  width: 186px;
  background-color: #B0C7F5;
  border-radius: 4px;
  font-size: 12px;
  font-family: mon-600;
  color: #232323;
  transition: all .8 ease;
  transform-origin: 9px 0px;
  animation: shaking .8s ease infinite;

  > img {
    height: 24px;
    width: 24px;
  }

  &::before {
    content: '';
    position: absolute;
    top: -14px;
    left: 9px;
    display: block;
    width: 0;
    height: 0;
    border: 9px solid transparent;
    border-bottom-color: #B0C7F5;
    border-radius: 10px;
  }

  @keyframes shaking {
    0% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-1px);
    }
    100% {
      transform: translateY(0);
    }
  }
}

.category-search-alert-enter-active,
.category-search-alert-leave-active {
  transition: all 0.5s ease-in-out;
  animation: none;
}

.category-search-alert-enter-from,
.category-search-alert-leave-to {
  opacity: 0;
  transform: scale(0);
}

.filter-item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  height: 36px;
  background-color: #f5f5f5;
  border-radius: 20px;
  flex-shrink: 0;
  font-size: 12px;
  font-family: mon-600;
  color: #232323;
  transition: width 0.3s ease;

  + .filter-item {
    margin-left: 8px;
  }

  &--active {
    background-color: #052784;
    color: white;
  }

  &__nums {
    position: absolute;
    top: -8px;
    right: -5px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #009de0;
    font-size: 10px;
    font-family: mon-600;
    color: white;
    border: 1px solid white;
    border-radius: 50%;
  }

  &__icon {
    padding: 8px;
    font-size: 20px;
  }

  &__hover {
    z-index: 99;
    position: fixed;
    right: 12px;
    bottom: 64px;
    padding: 0;
    height: 40px;
    width: 40px;
    border: 1px solid #EBF1F5;

    > .filter-item__icon {
      font-size: 24px;
    }
  }
}

.custom-filters-dialog {
  &.common {
    ::v-deep(.popup-content) {
      padding: 0 16px 12px;
      background-color: transparent;
      backdrop-filter: none;
      border-radius: 8px;
    }
  }

  .filter-dialog {
    height: 100%;
    width: 100%;
    background-color: white;
    border-radius: 8px;

    &-item {
      padding-bottom: 16px;
    }

    &-divider {
      margin: 0 16px;
      height: 1px;
      background-color: #EBF1F5;
    }

    &.full-filters {
      position: relative;
      display: flex;
      flex-direction: column;
      background-color: transparent;
      padding: 12px 16px;

      .full-filters-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 12px;
        background-color: white;
        border-radius: 8px;
        height: 48px;
        margin-bottom: 12px;
        .header-title {
          font-family: mon-600;
          font-size: 14px;
          color: #232323;
        }
        .close-button {
          height: 20px;
        }
      }

      .full-filters-content {
        flex: 1;
        padding: 8px 0;
        background-color: white;
        border-radius: 8px 8px 0 0;
        overflow: scroll;

        .content {
          position: relative;
          margin-bottom: 0;
          padding-bottom: 0.5px;
          overflow: hidden;
          transition: height .3s ease;
        }
      }
    }

    .header {
      position: relative;
      padding: 0 16px;
      height: 52px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      .header-title {
        font-size: 12px;
        font-family: mon-600;
        color: #5c5c5c;
      }

      .clear-button {
        position: absolute;
        right: 52px;
        top: 18px;
        color: #007EB3;
        font-family: mon-500;
        font-size: 12px;
      }
    }
    .content {
      position: relative;
      padding: 0 16px;
      max-height: calc(100vh - 250px);
      margin-bottom: 16px;
      overflow-y: scroll;

      .content-item {
        height: 42px;
        display: flex;
        align-items: center;
        justify-content: space-between;

        &.show-more {
          position: absolute;
          bottom: 16px;
          height: 32px;
          width: 100%;
          color: #009de0;
          font-size: 12px;
          font-family: mon-500;
          background-color: white;
        }
        .content-item-title {
          font-size: 14px;
          font-family: mon-500;
          color: #232323;
        }
        .content-item-radio {
          font-size: 20px;
        }
      }

      .price-range {
        display: flex;
        gap: 8px;
        align-items: center;
        color: #232323;
        font-size: 12px;
        font-family: mon-500;

        input {
          text-align: center;
          height: 36px;
          line-height: 36px;
          width: 50%;
          border: 1px solid #EBF1F5;
          outline: none;
          border-radius: 4px;
          font-size: 12px;
          font-family: mon-500;
          transition: all .2s ease-in-out;

          &:focus {
            border: 1.5px solid #232323;
            background-color: #F8F8F8;
          }

          &::placeholder {
            color: #b2b7c2;
          }
        }
      }
    }

    .footer {
      width: 100%;
      background-color: #FFFFFF;
      border-radius: 0 0 8px 8px;
      border-top: 0.5px solid #e4e6eb;
      padding: 12px 16px 16px;
      display: flex;
      justify-content: center;
      gap: 11px;

      .reset-button, .done-button {
        width: 100%;
        height: 44px;
        line-height: 44px;
        text-align: center;
        border-radius: 22px;
        font-size: 14px;
        font-family: mon-600;
        color: #232323;
      }
      .reset-button {
        background: #F8F8F8;
      }
      .done-button {
        background-color: #052785;
        color: white;
      }
    }
  }
}
</style>
